import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import trackEvents from '../constants/track-events'
import { useNotificationContext } from '../context'
import { query } from '../graphql'
import { notifyBugsnag } from '../helpers'
import { trackEvent } from '../helpers/analytics'
import {
  buildBundleFilename,
  buildBundleVariables,
  canExportBundle,
  downloadBlob,
  getAvailableProfileTypes,
  getBundleDocuments,
  parseGeneratePdfResponse
} from '../helpers/pdf-bundle'
import { listDocuments, parseAdditionalData } from '../helpers/talent-details'
import { useSpaceFiles } from './use-space-files'

// AppSync bricht bei 30 Sekunden ab, der Watchdog liegt bewusst darueber. Er
// greift nur, wenn die Verbindung haengt und gar keine Antwort mehr kommt.
const BUNDLE_TIMEOUT = 1000 * 40

/**
 * Was fuer dieses Talent in eine gemeinsame PDF kann. Dialog, Aktionen-Menue
 * und der Link unter ANHAENGE lesen dieselbe Quelle, damit alle drei zur
 * selben Zeit auftauchen und dasselbe anbieten.
 */
export const usePdfBundleOptions = ({ space, career }) => {
  const {
    data: files = [],
    isPending: isLoadingDocuments,
    isError: isDocumentsError
  } = useSpaceFiles(space?.id)

  // Die Talent-Seite ruft den Hook vor ihren Lade-Returns auf, dann gibt es
  // den Space noch nicht.
  if (!space) {
    return {
      profileTypes: [],
      documents: [],
      isLoadingDocuments,
      isDocumentsError,
      canExport: false
    }
  }

  const { fileFields } = parseAdditionalData(space)
  const documents = getBundleDocuments(listDocuments(files, fileFields))
  const profileTypes = getAvailableProfileTypes({
    progress: space.progress,
    careerId: career?.id
  })

  return {
    profileTypes,
    documents,
    isLoadingDocuments,
    isDocumentsError,
    canExport: canExportBundle({ profileTypes, documents })
  }
}

/**
 * Der Export selbst. Der Hook sitzt auf der Talent-Seite und nicht im Dialog,
 * damit der Lauf weitergeht, wenn der Kunde den Dialog schliesst, und der Link
 * unter ANHAENGE so lange "PDF wird erstellt" zeigt.
 */
export const usePdfBundleExport = () => {
  const { t } = useTranslation()
  const { success, error } = useNotificationContext()

  // Space, fuer den gerade ein PDF entsteht, sonst null. Wer waehrenddessen zu
  // einem anderen Talent wechselt, sieht dort keinen fremden Ladezustand.
  const [loadingSpaceId, setLoadingSpaceId] = useState(null)

  // Der Lauf dauert 10 bis 20 Sekunden. inFlightRef sperrt den zweiten Klick,
  // weil setState asynchron ist, isMountedRef haelt State-Updates von einer
  // Komponente fern, die der Kunde inzwischen verlassen hat.
  const inFlightRef = useRef(false)
  const isMountedRef = useRef(true)

  useEffect(
    () => () => {
      isMountedRef.current = false
    },
    []
  )

  /**
   * Anders als beim Einzeldownload antwortet das Backend nicht mit einem
   * Redirect auf die Renderseite, sondern mit einer signierten S3-URL. Deshalb
   * laeuft das inline statt in einem neuen Tab, und deshalb bekommt die Datei
   * einen eigenen Namen statt aivy-report.pdf.
   *
   * @returns {Promise<boolean>} true, wenn die Datei heruntergeladen wurde
   */
  const exportBundle = useCallback(
    async ({
      space,
      careerId,
      diagnosticVersion,
      language,
      types,
      documentNames = [],
      source
    }) => {
      if (inFlightRef.current) return false

      inFlightRef.current = true
      setLoadingSpaceId(space.id)

      try {
        const response = await Promise.race([
          query({
            query: 'generatePdfBundle',
            variables: buildBundleVariables({
              spaceId: space.id,
              userId: space.owner,
              careerId,
              language,
              types,
              documentNames
            }),
            // Das GraphQL-Feld heisst generatePdf, der Export generatePdfBundle.
            options: { origin: 'generatePdf' }
          }),
          new Promise((resolve, reject) => {
            setTimeout(
              () => reject(new Error('BUNDLE_TIMEOUT')),
              BUNDLE_TIMEOUT
            )
          })
        ])

        const { ok, pdfUrl, contentLength, skippedDocuments, reason } =
          parseGeneratePdfResponse(response)
        if (!ok) throw new Error(`generatePdf bundle: ${reason}`)

        const file = await fetch(pdfUrl)
        if (!file.ok) {
          throw new Error(`generatePdf bundle fetch: ${file.status}`)
        }

        // Der Download haengt bewusst nicht am Mount. Wer waehrend der Wartezeit
        // das Talent wechselt, bekommt seine Datei trotzdem.
        downloadBlob({
          blob: await file.blob(),
          filename: buildBundleFilename({
            name: space.name,
            fallback: space.identification
          })
        })

        // Die Datei ist da, deshalb kein Fehler-Toast. Der Kunde soll aber
        // wissen, dass ein Dokument (verschluesselt, defekt) darin fehlt.
        success(
          skippedDocuments.length > 0
            ? t('pages.talent.pdf_bundle_documents_skipped', {
                count: skippedDocuments.length
              })
            : t('pdf_success')
        )

        trackEvent(trackEvents.DOWNLOAD_ATTACHMENTS_BUNDLE, {
          diagnostic_version: diagnosticVersion,
          language,
          types: types.join(','),
          document_count: documentNames.length,
          skipped_count: skippedDocuments.length,
          source,
          content_length: contentLength
        })

        return true
      } catch (err) {
        // query() zeigt bei fehlender Berechtigung bereits selbst eine Meldung.
        if (err.message !== 'NOT_AUTHORIZED') {
          error(t('pages.talent.attachments_bundle_error'))
          notifyBugsnag(err)
        }

        return false
      } finally {
        inFlightRef.current = false
        if (isMountedRef.current) setLoadingSpaceId(null)
      }
    },
    [t, success, error]
  )

  return {
    exportBundle,
    isLoadingFor: (spaceId) => !!spaceId && loadingSpaceId === spaceId
  }
}
